import { toast as sonnerToast } from 'sonner';

/** Match Hookshot IDE delete undo window. */
export const TOAST_UNDO_DURATION_MS = 5000;

type UndoableToastOptions = {
  message: string;
  description?: string;
  /** Restore optimistic UI when undoing a pending delete. */
  onUndo?: () => void | Promise<void>;
  /**
   * Commit the destructive action after the undo window.
   * Prefer this over deleting immediately so Undo can cancel without a restore API.
   */
  onCommit?: () => void | Promise<void>;
  duration?: number;
};

/**
 * Destructive-action toast with an Undo affordance (Hookshot IDE pattern).
 * Prefer delaying `onCommit` until after the window so Undo can cancel without a restore API.
 */
function undoable({
  message,
  description,
  onUndo,
  onCommit,
  duration = TOAST_UNDO_DURATION_MS,
}: UndoableToastOptions) {
  let undone = false;

  const timeoutId = onCommit
    ? setTimeout(() => {
        if (!undone) {
          void onCommit();
        }
      }, duration)
    : undefined;

  return sonnerToast(message, {
    description,
    duration,
    action: {
      label: 'Undo',
      onClick: () => {
        undone = true;
        if (timeoutId !== undefined) {
          clearTimeout(timeoutId);
        }
        void onUndo?.();
      },
    },
  });
}

function warn(...args: Parameters<NonNullable<typeof sonnerToast.warning>>) {
  // Sonner omits `warning` when this module is evaluated in a Node/server bundle
  // (Next.js "collecting page data"). Binding it at import time crashes the build.
  if (typeof sonnerToast.warning === 'function') {
    return sonnerToast.warning(...args);
  }
  return sonnerToast(...args);
}

function teachingPending({
  description,
  teachYourAiHref,
}: {
  description?: string;
  teachYourAiHref?: string | null;
} = {}) {
  return sonnerToast.success('Teaching recorded', {
    position: 'bottom-center',
    className: 'teaching-pending',
    description:
      description ?? 'Pending approval before it is used to train the model.',
    action: teachYourAiHref
      ? {
          label: 'Open Teach your AI',
          onClick: () => {
            window.location.assign(teachYourAiHref);
          },
        }
      : undefined,
  });
}

/** Drop-in replacement for react-toastify's `toast`, plus Hookshot-style undo. */
export const toast = Object.assign(sonnerToast, {
  /** react-toastify alias */
  warn,
  undoable,
  teachingPending,
});
